<script>
import { mapGetters } from 'vuex';
import V4Button from 'dashboard/components-next/button/Button.vue';
import DropdownMenu from 'dashboard/components-next/dropdown-menu/DropdownMenu.vue';
import { useAlert, useTrack } from 'dashboard/composables';
import fromUnixTime from 'date-fns/fromUnixTime';
import format from 'date-fns/format';
import {
  buildMessageReportData,
  buildMessageReportFileName,
  exportMessageReportCsv,
  exportMessageReportPdf,
} from 'dashboard/helper/messageReportExporter';
import ReportFilterSelector from './components/FilterSelector.vue';
import { GROUP_BY_FILTER } from './constants';
import { REPORTS_EVENTS } from '../../../../helper/AnalyticsHelper/events';
import ReportContainer from './ReportContainer.vue';
import SentMessagesPanel from './SentMessagesPanel.vue';
import WhatsAppTemplatesCard from './WhatsAppTemplatesCard.vue';

const REPORTS_KEYS = {
  CONVERSATIONS: 'conversations_count',
  ACTIVE_CONVERSATIONS: 'active_conversations_count',
  INCOMING_MESSAGES: 'incoming_messages_count',
  OUTGOING_MESSAGES: 'outgoing_messages_count',
  TEMPLATE_MESSAGES: 'template_messages_count',
  API_MESSAGES: 'api_messages_count',
  IA_MESSAGES: 'ia_messages_count',
  AUTOMATED_MESSAGES: 'automated_messages_count',
  DELIVERED_MESSAGES: 'delivered_messages_count',
  UNDELIVERED_MESSAGES: 'undelivered_messages_count',
  FIRST_RESPONSE_TIME: 'avg_first_response_time',
  RESOLUTION_TIME: 'avg_resolution_time',
  RESOLUTION_COUNT: 'resolutions_count',
  REPLY_TIME: 'reply_time',
};

export default {
  name: 'ConversationReports',
  components: {
    ReportFilterSelector,
    ReportContainer,
    SentMessagesPanel,
    WhatsAppTemplatesCard,
    V4Button,
    DropdownMenu,
  },
  data() {
    return {
      from: 0,
      to: 0,
      groupBy: GROUP_BY_FILTER[1],
      businessHours: false,
      showExportMenu: false,
    };
  },
  computed: {
    ...mapGetters({
      globalConfig: 'globalConfig/get',
      accountById: 'accounts/getAccount',
    }),
    currentAccount() {
      return this.accountById(Number(this.$route.params.accountId)) || {};
    },
    exportMenuItems() {
      return [
        {
          action: 'export',
          value: 'csv',
          label: this.$t('REPORT.EXPORT_MENU.CSV'),
          icon: 'i-ph-file-csv',
        },
        {
          action: 'export',
          value: 'pdf',
          label: this.$t('REPORT.EXPORT_MENU.PDF'),
          icon: 'i-ph-file-pdf',
        },
      ];
    },
  },
  methods: {
    fetchAllData() {
      this.fetchAccountSummary();
      this.fetchChartData();
    },
    fetchAccountSummary() {
      try {
        this.$store.dispatch('fetchAccountSummary', this.getRequestPayload());
      } catch {
        useAlert(this.$t('REPORT.SUMMARY_FETCHING_FAILED'));
      }
    },
    fetchChartData() {
      [
        'CONVERSATIONS',
        'ACTIVE_CONVERSATIONS',
        'INCOMING_MESSAGES',
        'OUTGOING_MESSAGES',
        'TEMPLATE_MESSAGES',
        'API_MESSAGES',
        'IA_MESSAGES',
        'AUTOMATED_MESSAGES',
        'DELIVERED_MESSAGES',
        'UNDELIVERED_MESSAGES',
        'FIRST_RESPONSE_TIME',
        'RESOLUTION_TIME',
        'RESOLUTION_COUNT',
        'REPLY_TIME',
      ].forEach(async key => {
        try {
          await this.$store.dispatch('fetchAccountReport', {
            metric: REPORTS_KEYS[key],
            ...this.getRequestPayload(),
          });
        } catch {
          useAlert(this.$t('REPORT.DATA_FETCHING_FAILED'));
        }
      });
    },
    getRequestPayload() {
      const { from, to, groupBy, businessHours } = this;

      return {
        from,
        to,
        groupBy: groupBy?.period,
        businessHours,
      };
    },
    // Builds the messages report (received / sent / total per period) straight
    // from the timeseries already loaded in the store for the charts, so no
    // extra request is made.
    getMessageReportContext() {
      const reports = this.$store.getters.getAccountReports?.data || {};
      const { rows, totals } = buildMessageReportData({
        incoming: reports.incoming_messages_count || [],
        outgoing: reports.outgoing_messages_count || [],
        undelivered: reports.undelivered_messages_count || [],
        api: reports.api_messages_count || [],
        ia: reports.ia_messages_count || [],
        automated: reports.automated_messages_count || [],
        template: reports.template_messages_count || [],
        groupBy: this.groupBy?.period || 'day',
      });
      return { rows, totals };
    },
    messageReportLabels() {
      return {
        date: this.$t('REPORT.MESSAGE_REPORT.DATE'),
        received: this.$t('REPORT.MESSAGE_REPORT.RECEIVED'),
        sent: this.$t('REPORT.MESSAGE_REPORT.SENT'),
        delivered: this.$t('REPORT.MESSAGE_REPORT.DELIVERED'),
        viaAttendant: this.$t('REPORT.MESSAGE_REPORT.VIA_ATTENDANT'),
        viaIa: this.$t('REPORT.MESSAGE_REPORT.VIA_IA'),
        viaApi: this.$t('REPORT.MESSAGE_REPORT.VIA_API'),
        viaAutomated: this.$t('REPORT.MESSAGE_REPORT.VIA_AUTOMATED'),
        template: this.$t('REPORT.MESSAGE_REPORT.TEMPLATE'),
        session: this.$t('REPORT.MESSAGE_REPORT.SESSION'),
        total: this.$t('REPORT.MESSAGE_REPORT.TOTAL'),
        totalRow: this.$t('REPORT.MESSAGE_REPORT.TOTAL_ROW'),
      };
    },
    messageReportPeriod() {
      return this.$t('REPORT.MESSAGE_REPORT.PERIOD', {
        from: format(fromUnixTime(this.from), 'dd/MM/yyyy'),
        to: format(fromUnixTime(this.to), 'dd/MM/yyyy'),
      });
    },
    messageReportAccountLine() {
      return this.$t('REPORT.MESSAGE_REPORT.ACCOUNT', {
        name: this.currentAccount?.name || '-',
      });
    },
    messageReportGeneratedAt() {
      return this.$t('REPORT.MESSAGE_REPORT.GENERATED_AT', {
        datetime: format(new Date(), 'dd/MM/yyyy HH:mm'),
      });
    },
    messageReportFooterTotal(totals) {
      return this.$t('REPORT.MESSAGE_REPORT.GRAND_TOTAL', {
        total: totals.total.toLocaleString(),
        received: totals.received.toLocaleString(),
        sent: totals.sent.toLocaleString(),
        delivered: totals.delivered.toLocaleString(),
      });
    },
    messageReportFileName(extension) {
      return buildMessageReportFileName({
        accountName: this.currentAccount?.name,
        from: this.from,
        to: this.to,
        groupBy: this.groupBy?.period,
        extension,
      });
    },
    onExportAction({ value }) {
      this.showExportMenu = false;
      if (value === 'csv') {
        this.exportMessagesCsv();
      } else if (value === 'pdf') {
        this.exportMessagesPdf();
      }
    },
    exportMessagesCsv() {
      const { rows, totals } = this.getMessageReportContext();
      if (!rows.length) {
        useAlert(this.$t('REPORT.MESSAGE_REPORT.NO_DATA'));
        return;
      }
      exportMessageReportCsv({
        rows,
        totals,
        labels: this.messageReportLabels(),
        account: this.messageReportAccountLine(),
        period: this.messageReportPeriod(),
        generatedAt: this.messageReportGeneratedAt(),
        fileName: this.messageReportFileName('csv'),
      });
      useTrack(REPORTS_EVENTS.DOWNLOAD_REPORT, {
        reportType: 'messages_csv',
        businessHours: this.businessHours,
      });
    },
    async exportMessagesPdf() {
      const { rows, totals } = this.getMessageReportContext();
      if (!rows.length) {
        useAlert(this.$t('REPORT.MESSAGE_REPORT.NO_DATA'));
        return;
      }
      try {
        await exportMessageReportPdf({
          rows,
          totals,
          labels: this.messageReportLabels(),
          title: this.$t('REPORT.MESSAGE_REPORT.TITLE'),
          account: this.messageReportAccountLine(),
          period: this.messageReportPeriod(),
          generatedAt: this.messageReportGeneratedAt(),
          footerTotal: this.messageReportFooterTotal(totals),
          fileName: this.messageReportFileName('pdf'),
          logoUrl: this.globalConfig?.logo,
        });
        useTrack(REPORTS_EVENTS.DOWNLOAD_REPORT, {
          reportType: 'messages_pdf',
          businessHours: this.businessHours,
        });
      } catch {
        useAlert(this.$t('REPORT.MESSAGE_REPORT.EXPORT_FAILED'));
      }
    },
    onFilterChange({ from, to, groupBy, businessHours }) {
      this.from = from;
      this.to = to;
      this.groupBy = groupBy;
      this.businessHours = businessHours;
      this.fetchAllData();

      useTrack(REPORTS_EVENTS.FILTER_REPORT, {
        filterValue: { from, to, groupBy, businessHours },
        reportType: 'conversations',
      });
    },
  },
};
</script>

<template>
  <div class="flex flex-col">
    <div class="flex flex-col items-start w-full pt-4 pb-4">
      <div class="flex items-center justify-between w-full gap-4">
        <h1
          class="text-2xl font-semibold font-interDisplay tracking-[0.3px] text-slate-900 dark:text-slate-25"
        >
          <!-- DESABILITADO: Menu sempre aberto -->
          <!-- <woot-sidemenu-icon /> -->
          {{ $t('REPORT.HEADER') }}
        </h1>
        <div class="relative flex items-center">
          <V4Button
            :label="$t('REPORT.EXPORT_MENU.LABEL')"
            icon="i-ph-download-simple"
            size="sm"
            @click="showExportMenu = !showExportMenu"
          />
          <DropdownMenu
            v-if="showExportMenu"
            v-on-clickaway="() => (showExportMenu = false)"
            :menu-items="exportMenuItems"
            class="mt-2 top-full ltr:right-0 rtl:left-0"
            @action="onExportAction"
          />
        </div>
      </div>
    </div>
    <div class="flex flex-col gap-3">
      <ReportFilterSelector
        :show-agents-filter="false"
        show-group-by-filter
        @filter-change="onFilterChange"
      />
      <SentMessagesPanel :group-by="groupBy" />
      <WhatsAppTemplatesCard :group-by="groupBy" />
      <ReportContainer :group-by="groupBy" />
    </div>
  </div>
</template>
